import { CdkDragDrop } from '@angular/cdk/drag-drop';
import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { formatNotationSegment } from '../../../utils/question-numbering.util';
import { FormOverall } from '../re-orderable-theme.component';

export type FormGroupQuestion = FormGroup<{
  question_id: FormControl<string>;
  question: FormControl<string>;
  theme: FormControl<string>;
  label: FormControl<string>;
  parent_question_id: FormControl<string | null>;
  children: FormArray<FormGroupQuestion>;
}>;

@Component({
  selector: 'app-question-node',
  templateUrl: './question-node.component.html',
  styleUrls: ['./question-node.component.scss'],
  standalone: false,
})
export class QuestionNodeComponent implements OnInit, OnChanges {
  @Input() question: FormGroupQuestion;
  @Input() parentForm: FormOverall;
  @Input() themeIndex: number;
  @Input() depth = 0;
  @Input() index = 0;
  @Input() offset = 0;
  @Input() showErrors = false;

  @Input() addQuestion: (
    themeIndex: number,
    parent?: FormGroupQuestion,
    index?: number
  ) => void;
  @Input() removeQuestion: (
    themeIndex: number,
    parent?: FormGroupQuestion,
    index?: number
  ) => void;

  maxDepth = 2;

  label: string;

  get children() {
    return this.question.controls.children;
  }

  get width() {
    return `calc(100% - ${this.depth * 40})`;
  }

  get dropListId() {
    return this.toDropListId(this.questionId);
  }

  get inputId() {
    return this.toInputId(this.questionId);
  }

  get questionId() {
    return this.question.controls.question_id.value;
  }

  get connectedDropLists() {
    if (!this.question) {
      return [];
    }

    return this.collectDropListIds(this.question);
  }

  get shouldHighlightQuestion() {
    if (!this.showErrors) {
      return false;
    }

    const questionControl = this.question.controls.question;

    return (
      questionControl.hasError('duplicateQuestionNames') ||
      (questionControl.invalid &&
        (questionControl.dirty || questionControl.touched))
    );
  }

  ngOnInit() {
    this.label = this.calculateLabel();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.index || changes.depth || changes.offset) {
      this.label = this.calculateLabel();
    }
  }

  onKeydown(event: KeyboardEvent): void {
    if (this.indentOnTab(event)) {
      return;
    }

    this.appendNodeOnEnter(event);
  }

  onChildDrop(event: CdkDragDrop<FormGroupQuestion[]>) {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    const children = this.children;
    const moved = children.at(event.previousIndex);

    children.removeAt(event.previousIndex);
    children.insert(event.currentIndex, moved);
  }

  private appendNodeOnEnter(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey && !event.ctrlKey) {
      event.preventDefault();

      const parentQuestion = this.findParentQuestion(
        this.parentForm,
        this.question.controls.parent_question_id.value
      );

      this.addQuestion(this.themeIndex, parentQuestion, this.index + 1);
    }
  }

  private indentOnTab(event: KeyboardEvent): boolean {
    if (
      event.key !== 'Tab' ||
      event.shiftKey ||
      event.ctrlKey ||
      event.altKey
    ) {
      return false;
    }

    if (!this.parentForm || this.depth >= this.maxDepth) {
      return false;
    }

    const subtreeDepth = this.getSubtreeMaxDepth(this.question);

    if (this.depth + 1 + subtreeDepth > this.maxDepth) {
      return false;
    }

    const context = this.findQuestionContext(this.parentForm, this.questionId);

    if (!context || context.index <= 0) {
      return false;
    }

    const newParent = context.array.at(context.index - 1);

    if (!newParent) {
      return false;
    }

    event.preventDefault();

    context.array.removeAt(context.index);
    newParent.controls.children.push(this.question);
    this.question.controls.parent_question_id.setValue(
      newParent.controls.question_id.value
    );

    setTimeout(() => {
      const element = document.getElementById(this.inputId);

      if (element) {
        element.focus();
      }
    });

    return true;
  }

  private findParentQuestion(
    form: FormOverall,
    parentQuestionId: string
  ): FormGroupQuestion | null {
    if (!parentQuestionId) {
      return null;
    }

    for (const theme of form.controls.themes.controls) {
      const found = this.searchQuestionInArray(
        theme.controls.questions,
        parentQuestionId
      );

      if (found) {
        return found;
      }
    }

    return null;
  }

  private searchQuestionInArray(
    questions: FormArray<FormGroupQuestion>,
    questionId: string
  ): FormGroupQuestion | null {
    for (const question of questions.controls) {
      if (question.controls.question_id.value === questionId) {
        return question;
      }

      const foundInChildren = this.searchQuestionInArray(
        question.controls.children,
        questionId
      );

      if (foundInChildren) {
        return foundInChildren;
      }
    }

    return null;
  }

  private findQuestionContext(
    form: FormOverall,
    questionId: string
  ): { array: FormArray<FormGroupQuestion>; index: number } | undefined {
    return form.controls.themes.controls
      .map((theme) =>
        this.findQuestionInArray(theme.controls.questions, questionId)
      )
      .find(Boolean);
  }

  private findQuestionInArray(
    questions: FormArray<FormGroupQuestion>,
    questionId: string
  ): { array: FormArray<FormGroupQuestion>; index: number } | null {
    const index = questions.controls.findIndex(
      (question) => question.controls.question_id.value === questionId
    );

    if (index > -1) {
      return { array: questions, index };
    }

    return questions.controls
      .map((q) => this.findQuestionInArray(q.controls.children, questionId))
      .find(Boolean);
  }

  private collectDropListIds(question: FormGroupQuestion) {
    if (!question.controls.children.length) {
      return [];
    }

    return [this.toDropListId(question.controls.question_id.value)].concat(
      question.controls.children.controls.flatMap((child) =>
        this.collectDropListIds(child)
      )
    );
  }

  private toDropListId(questionId: string) {
    return `question-children-${questionId}`;
  }

  private toInputId(questionId: string) {
    return `question-input-${questionId}`;
  }

  private getSubtreeMaxDepth(question: FormGroupQuestion): number {
    if (!question.controls.children.length) {
      return 0;
    }

    return (
      1 +
      Math.max(
        ...question.controls.children.controls.map((child) =>
          this.getSubtreeMaxDepth(child)
        )
      )
    );
  }

  private calculateLabel(): string {
    return formatNotationSegment(this.depth, this.index + this.offset);
  }
}
